'use client';

import React from 'react';
import debounce from 'lodash/debounce';
import { fuzzyMatch, type FuzzyMatches } from '@nozbe/microfuzz';
import { Highlight } from '@nozbe/microfuzz/react';
import {
  Box,
  Stack,
  Drawer,
  useMediaQuery,
  Button,
  Typography,
  StateCard,
  List,
  ListItem,
  ListItemButton,
  Collapse,
  ListItemIcon,
} from '@unity/cloud-ui';
import { styled } from '@unity/cloud-ui/styles';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowElbowLeftUpIcon, MagnifyingGlassIcon, CaretRightIcon, CaretDownIcon } from '@phosphor-icons/react';
import { usePathname } from '@/i18n/routing';
import { SidebarDataType, SidebarItem } from '@/lib/content-loader/types';

import type { ManifestSchema } from '@/lib/content-loader/schemas/manifest.schema';
import { normalizeInternalPath } from '@/utils/normalize-internal-path';
import { scrollToAnchor } from '@/utils/scroll-to-anchor';
import { getLocation } from '@/utils/get-location';
import themeConstants from '../theme/theme-constants';
import { NavigationLink } from '../navigation-link';
import { useIsInternal } from '../request-provider/request-provider';
import { SidebarContext } from './sidebar-provider';
import { SidebarFilter } from './components/sidebar-filter';

type SidebarItemWithFuzzyMatches = SidebarItem & { matches?: FuzzyMatches };

const SidebarDrawer = styled(Drawer)(({ theme }) => ({
  width: '75vw',
  flexShrink: 0,
  maxWidth: theme.layout.sidebarDrawerMaxWidth,
  [`& .MuiDrawer-paper`]: {
    width: '75vw',
    maxWidth: theme.layout.sidebarDrawerMaxWidth,
    borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
  },
  [`& .MuiDrawer-paper > div`]: {
    padding: 0,
  },
}));

type ParentSidebar = NonNullable<ManifestSchema>['sidebars'][number];

function SectionItemsList(props: {
  parentKey: string;
  items: SidebarItemWithFuzzyMatches[];
  level: number;
  pathname: string;
  currentHash: string;
  onItemClick: (id: string, item: SidebarItem, e: React.MouseEvent, currentlyOpen: boolean) => void;
  expandedItems: Set<string>;
  manuallyToggledItems: Set<string>;
  filterMode: boolean;
}) {
  const {
    items,
    level,
    pathname,
    currentHash,
    onItemClick,
    expandedItems,
    manuallyToggledItems,
    filterMode,
    parentKey,
  } = props;

  const hasExactMatch = (items: SidebarItemWithFuzzyMatches[], fullPath: string): boolean =>
    items.some((item) => {
      if (item.href === fullPath) {
        return true;
      }
      return item.items.length > 0 ? hasExactMatch(item.items, fullPath) : false;
    });

  const isChildrenActive = (items: SidebarItemWithFuzzyMatches[], fullPath: string): boolean =>
    items.some((item) => {
      if (item.href === fullPath) {
        return true;
      }

      return item.items.length > 0 ? isChildrenActive(item.items, fullPath) : false;
    });

  return (
    <List disablePadding sx={{ ml: level * 0.5 }}>
      {items.map((item) => {
        const id = `${parentKey}-${item.title}`;
        const fullPath = pathname + currentHash;

        const isActive = fullPath === item.href || (item.href === pathname && !hasExactMatch(items, fullPath));

        const isSectionOpen = isActive || isChildrenActive(item.items, fullPath);
        const open = filterMode || (manuallyToggledItems.has(id) ? expandedItems.has(id) : isSectionOpen);

        // If this section is collapsed but contains the active page, highlight it
        const hasActiveChild = !isActive && isSectionOpen;
        const shouldHighlightAsActive = isActive || (hasActiveChild && !open);

        const handleItemClickWithState = (e: React.MouseEvent) => {
          onItemClick(id, item, e, open);
        };

        return (
          <React.Fragment key={id}>
            <ListItem sx={{ '&:hover': { backgroundColor: 'transparent !important' } }}>
              <ListItemButton
                onClick={handleItemClickWithState}
                color="default"
                component={item.href ? NavigationLink : 'div'}
                href={item.href}
                selected={shouldHighlightAsActive}
                aria-current={shouldHighlightAsActive ? 'page' : undefined}
                sx={{ my: 0.25, py: 0.5 }}
              >
                <ListItemIcon sx={{ width: '16px', color: 'text.primary' }}>
                  {item.items.length > 0 && (open ? <CaretDownIcon weight="fill" /> : <CaretRightIcon weight="fill" />)}
                </ListItemIcon>
                <Typography variant="subtitle2" component="div">
                  <Highlight
                    ranges={item.matches?.[0] || null}
                    text={item.title}
                    style={{ fontWeight: 'bold', textDecoration: 'underline' }}
                  />
                </Typography>
              </ListItemButton>
            </ListItem>
            {item.items.length > 0 && (
              <Collapse in={open} timeout={0}>
                <SectionItemsList
                  parentKey={id}
                  items={item.items}
                  level={level + 1}
                  pathname={pathname}
                  currentHash={currentHash}
                  onItemClick={onItemClick}
                  expandedItems={expandedItems}
                  manuallyToggledItems={manuallyToggledItems}
                  filterMode={filterMode}
                />
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );
}

const INTL_SEGMENTER_SUPPORTED = Intl && Intl.Segmenter;

const filterSections = (items: SidebarItem[], query: string, locale: string): SidebarItemWithFuzzyMatches[] => {
  const filteredItems: SidebarItemWithFuzzyMatches[] = [];

  items.forEach((item) => {
    const filteredSubItems = filterSections(item.items, query, locale);
    if (filteredSubItems.length > 0) {
      // if any sub-items match, include this item with the filtered sub-items
      filteredItems.push({
        ...item,
        items: filteredSubItems,
      });
      return;
    }

    const match = fuzzyMatch(item.title, query);

    if (match) {
      filteredItems.push({
        ...item,
        items: [],
        matches: match.matches.reduce((acc, ranges) => {
          // filter out very small match ranges
          // extend the range to the word boundaries if Intl.Segmenter is supported
          const filtered = ranges?.reduce(
            (accRanges, [start, end]) => {
              if (end - start <= 0) {
                return accRanges;
              }
              if (INTL_SEGMENTER_SUPPORTED) {
                const segmenter = new Intl.Segmenter(locale, { granularity: 'word' });
                const segments = segmenter.segment(item.title);

                const matchingSegment = Array.from(segments).find(({ index, segment }) => {
                  const segmentStart = index || 0;
                  const segmentEnd = segmentStart + segment.length;
                  return start >= segmentStart && end <= segmentEnd;
                });

                if (matchingSegment) {
                  const segmentStart = matchingSegment.index || 0;
                  const segmentEnd = segmentStart + matchingSegment.segment.length - 1;
                  accRanges.push([segmentStart, segmentEnd]);
                } else {
                  accRanges.push([start, end]);
                }
              } else {
                accRanges.push([start, end]);
              }
              return accRanges;
            },
            [] as [number, number][],
          );

          return filtered ? [...acc, filtered] : acc;
        }, [] as FuzzyMatches),
      });
    }
  });

  return filteredItems;
};

export function Sidebar(props: { content: SidebarDataType | null; parentSidebar: ParentSidebar | null }) {
  const t = useTranslations();
  const {
    sidebarOpen,
    setSidebarState,
    setScrollPosition,
    scrollPosition,
    expandedItems,
    setExpandedItems,
    manuallyToggledItems,
  } = React.useContext(SidebarContext);

  const locale = useLocale();
  const pathname = usePathname();
  const isInternal = useIsInternal();
  const boxRef = React.useRef<HTMLDivElement | undefined>(undefined);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.between('xs', 'md'));
  const [searchValue, setSearchValue] = React.useState('');
  const [currentHash, setCurrentHash] = React.useState('');
  const inFilterMode = Boolean(searchValue);

  const normalizedPathname = React.useMemo(
    () => (isInternal ? '/internal' : '') + normalizeInternalPath(pathname.replace(new RegExp(`^/${locale}`, 'i'), '')),
    [pathname, locale, isInternal],
  );

  const sections = React.useMemo(() => {
    // Type guard: check if content is a regular sidebar (not a $ref)
    if (!props.content || '$ref' in props.content) {
      return [];
    }

    if (searchValue.length > 0) {
      return filterSections(props.content.sections || [], searchValue, locale);
    }
    return props.content.sections || [];
  }, [props.content, searchValue, locale]);

  React.useEffect(() => {
    // reset the current scrollTop position after
    // a sidebar navigation
    if (boxRef.current && scrollPosition) {
      const { overflowY = 'auto' } = window.getComputedStyle(boxRef.current);
      boxRef.current.style.overflowY = 'hidden';
      boxRef.current.scroll({ top: scrollPosition, behavior: 'instant' });
      boxRef.current.style.overflowY = overflowY;
    }
  }, [scrollPosition]);

  React.useEffect(() => {
    const updateHash = () => {
      const hash = getLocation().hash;
      // Strip query parameters from hash (e.g., #anchor?param=value -> #anchor)
      const cleanHash = hash.split('?')[0];
      setCurrentHash(cleanHash);
    };
    const initialHash = getLocation().hash;
    const cleanInitialHash = initialHash.split('?')[0];
    setCurrentHash(cleanInitialHash);

    window.addEventListener('hashchange', updateHash);

    return () => {
      window.removeEventListener('hashchange', updateHash);
    };
  }, []);

  const handleItemClick = React.useCallback(
    (id: string, item: SidebarItem, e: React.MouseEvent, currentlyOpen: boolean) => {
      const isToggleIcon = e.target instanceof SVGElement;
      const isLinkNavigation = Boolean(item.href);

      if (isToggleIcon) {
        e.preventDefault(); // prevent navigation on icon click
      }

      if (isLinkNavigation && !isToggleIcon) {
        if (item.href?.includes('#')) {
          const [, hash] = item.href.split('#');
          if (hash) {
            scrollToAnchor(hash, { behavior: 'smooth' });
            if (window.location.hash !== `#${hash}`) {
              window.location.hash = hash;
            }
            setCurrentHash(`#${hash}`);
          }
        }

        setSidebarState(false);
        // save the current scroll position of the sidebar if it's a link navigation
        if (boxRef.current) {
          const scrollPos =
            (boxRef.current?.getBoundingClientRect().top || 0) -
            (boxRef.current?.firstElementChild?.getBoundingClientRect().top || 0);
          setScrollPosition(scrollPos);
        }
      }

      // handle item expansion state
      const items = new Set(expandedItems);

      manuallyToggledItems.current.add(id);

      if (currentlyOpen) {
        items.delete(id);
      } else {
        items.add(id);
      }
      setExpandedItems(items);
    },
    [expandedItems, setScrollPosition, setExpandedItems, setSidebarState, manuallyToggledItems],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = React.useCallback(
    debounce((value: string | null) => {
      setSearchValue(value?.trim() || '');
    }, 75),
    [],
  );

  const renderSidebarContent = () => {
    // Type guard: check if content is a regular sidebar (not a $ref)
    const parentLink = (props.content && !('$ref' in props.content) ? props.content.parentLink : undefined) ?? {
      title: props.parentSidebar?.title,
      href: props.parentSidebar?.path,
    };

    return (
      <Stack spacing={2} sx={{ width: '100%', p: 2 }}>
        {parentLink.title && parentLink.href && (
          <Button
            fullWidth
            color="default"
            startIcon={<ArrowElbowLeftUpIcon size={18} />}
            component={NavigationLink}
            href={parentLink.href}
            aria-label={t('accessibility:navigate-back-to-item', { item: parentLink.title })}
            sx={{
              justifyContent: 'flex-start',
              '&:hover': {
                color: 'text.primary',
              },
            }}
          >
            <Typography noWrap component="span" variant="body2" fontWeight={500}>
              {parentLink.title}
            </Typography>
          </Button>
        )}
        <SidebarFilter searchValue={searchValue} onSearch={handleSearch} />
        {sections.length === 0 && inFilterMode && (
          <StateCard title={t('sidebar:no-search-results')} icon={MagnifyingGlassIcon} />
        )}
        {sections.map((section) => (
          <Stack spacing={2} key={section.title}>
            <Typography variant="h6" component="div">
              {section.title}
            </Typography>
            <Box>
              <SectionItemsList
                parentKey={section.title}
                items={section.items}
                level={0}
                pathname={normalizedPathname}
                currentHash={currentHash}
                onItemClick={handleItemClick}
                expandedItems={expandedItems}
                manuallyToggledItems={manuallyToggledItems.current}
                filterMode={inFilterMode}
              />
            </Box>
          </Stack>
        ))}
      </Stack>
    );
  };

  if (!props.content) {
    return null;
  }

  if (isMobile) {
    return (
      <SidebarDrawer anchor="left" open={sidebarOpen} onClose={() => setSidebarState(false)}>
        {renderSidebarContent()}
      </SidebarDrawer>
    );
  }

  return (
    <Box
      ref={boxRef}
      sx={{
        position: 'fixed',
        height: 'inherit',
        width: 'inherit',
        overflowY: 'auto',
        backgroundColor: 'background.paper',
        maxWidth: themeConstants.layout.sidebarDrawerMaxWidth,
        borderRight: (theme) => `1px solid ${theme.vars.palette.divider}`,
      }}
    >
      {renderSidebarContent()}
    </Box>
  );
}
